/**
 * <s-open-status> - Live "open now / closed" label for the store. Reads the
 * current weekday and time in the store's time zone (data-timezone, default
 * Europe/Brussels) in the browser, so cached page HTML never shows a stale state.
 *
 * Data attributes (written by Liquid):
 *   data-days    comma list of open weekdays (monday..sunday)
 *   data-open    opening time "HH:MM"
 *   data-close   closing time "HH:MM"
 *   data-day-names JSON object weekday -> translated name
 *   data-label-open / -closed-today / -closed-tomorrow / -closed-day
 *     translated labels; %time% and %day% are replaced.
 * Children: [data-status-text] receives the label, [data-status-dot] gets
 * data-open toggled for its colour. The element stays hidden until it has a state.
 */
const WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

class SOpenStatus extends HTMLElement {
  connectedCallback() {
    this.update();
    this.timer = setInterval(() => this.update(), 60000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    const timeZone = this.dataset.timezone || 'Europe/Brussels';
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
      }).formatToParts(new Date());
    } catch {
      return;
    }
    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    const today = get('weekday').toLowerCase();
    const minutesNow = parseInt(get('hour'), 10) * 60 + parseInt(get('minute'), 10);

    const toMinutes = (value) => {
      const [h, m] = (value || '0:0').split(':').map((n) => parseInt(n, 10) || 0);
      return h * 60 + m;
    };
    const openDays = (this.dataset.days || '').split(',').map((d) => d.trim()).filter(Boolean);
    const openAt = toMinutes(this.dataset.open);
    const closeAt = toMinutes(this.dataset.close);
    const time = this.dataset.open || '';

    let names = {};
    try {
      names = JSON.parse(this.dataset.dayNames || '{}');
    } catch {}

    let label;
    let isOpen = false;
    const openToday = openDays.includes(today);

    if (openToday && minutesNow >= openAt && minutesNow < closeAt) {
      isOpen = true;
      label = this.dataset.labelOpen;
    } else if (openToday && minutesNow < openAt) {
      label = this.dataset.labelClosedToday;
    } else {
      const start = WEEK.indexOf(today);
      let next = null;
      for (let i = 1; i <= 7; i++) {
        const day = WEEK[(start + i) % 7];
        if (openDays.includes(day)) {
          next = { day, offset: i };
          break;
        }
      }
      if (!next) return;
      label = next.offset === 1 ? this.dataset.labelClosedTomorrow : this.dataset.labelClosedDay;
      label = (label || '').replace('%day%', names[next.day] || next.day);
    }

    label = (label || '').replace('%time%', time);
    const text = this.querySelector('[data-status-text]');
    const dot = this.querySelector('[data-status-dot]');
    if (text) text.textContent = label;
    if (dot) dot.toggleAttribute('data-open', isOpen);
    this.toggleAttribute('data-open', isOpen);
    this.style.display = '';
  }
}

if (!customElements.get('s-open-status')) customElements.define('s-open-status', SOpenStatus);
